import { ComponentType, ReactNode, SVGProps } from 'react';

import {
  BookOpenIcon,
  CheckMarkIcon,
  FaceWinkIcon,
  FileUploadIcon,
  GlobeIcon,
  SettingsIcon,
  UsersIcon,
  VoiceIcon,
} from '@braskai/icons';

export type PaywallBenefit = {
  title: string;
  description: string;
  icon: ReactNode;
};

// Figma "base/border" #e5e5e5 has no ui-kit palette token
export const PAYWALL_CARD_BORDER_COLOR = '#E5E5E5';

export const PAYWALL_CARD_SHADOW = '0px 12px 8px rgba(16, 24, 40, 0.08), 0px -2px 3px rgba(16, 24, 40, 0.03)';

export const ENTERPRISE_PLAN_NAME = 'Enterprise';

export const DEFAULT_ALLOWED_PLAN_NAMES = ['Creator Pro', 'Business'];

export const ALL_PLANS_INCLUDE = [
  'AI dubbing in 135+ languages',
  'Voice cloning in 32 languages',
  'Automatic transcription & translation',
  'Built-in video editor',
];

export const PAYWALL_CHIP_LABELS: Record<string, string> = {
  Business: 'Most precise results',
  Creator: 'Best to start',
  'Creator Pro': 'Most popular',
};

const createIcon = (IconComponent: ComponentType<SVGProps<SVGSVGElement>>) => (
  <IconComponent height={20} width={20} />
);

type PaywallPlanBenefits = {
  features: PaywallBenefit[];
  minutesDescription: string;
};

export const PAYWALL_PLAN_BENEFITS: Record<string, PaywallPlanBenefits> = {
  Business: {
    features: [
      {
        title: 'Many markets at once',
        description: 'translate into multiple languages in one flow',
        icon: createIcon(GlobeIcon),
      },
      {
        title: 'Consistent terminology',
        description: 'keep brand and product terms aligned',
        icon: createIcon(BookOpenIcon),
      },
      {
        title: 'Faster team output',
        description: 'use 10 voices, 5 members and priority processing',
        icon: createIcon(UsersIcon),
      },
    ],
    minutesDescription: 'built for teams localizing content regularly',
  },
  Creator: {
    features: [
      {
        title: 'Try real projects',
        description: 'localize, edit and export your own videos',
        icon: createIcon(FaceWinkIcon),
      },
      {
        title: 'Customize voice cloning',
        description: 'tune cloned voices and create your own library',
        icon: createIcon(SettingsIcon),
      },
      {
        title: 'Simple solo workflow',
        description: 'best for occasional, self-serve localization',
        icon: createIcon(UsersIcon),
      },
    ],
    minutesDescription: 'for simple one-time projects',
  },
  'Creator Pro': {
    features: [
      {
        title: 'Lip-sync your translated videos',
        description: 'sync mouth movement with translated speech',
        icon: createIcon(FaceWinkIcon),
      },
      {
        title: 'Ready-to-publish edits',
        description: 'work with scripts, subtitles and voice settings',
        icon: createIcon(SettingsIcon),
      },
      {
        title: 'Work together with your team',
        description: 'share your subscription with 3 team members',
        icon: createIcon(UsersIcon),
      },
    ],
    minutesDescription: 'translate more videos, with extra minutes available',
  },
};

export const ENTERPRISE_BENEFITS: PaywallBenefit[] = [
  {
    title: 'Custom amount of minutes',
    description: 'for high-volume localization operations',
    icon: createIcon(CheckMarkIcon),
  },
  {
    title: 'Managed review',
    description: 'add human review for high-stakes output',
    icon: createIcon(VoiceIcon),
  },
  {
    title: 'Procurement-ready',
    description: 'use custom contracts and invoicing',
    icon: createIcon(FileUploadIcon),
  },
  {
    title: 'Dedicated support',
    description: 'Get success support for complex workflows',
    icon: createIcon(UsersIcon),
  },
];
